import React, { SetStateAction, createContext, useContext, useEffect, Dispatch, useRef, useState } from 'react'
import axios from 'axios'
import {
  CaseCategoryType, CaseStatusType, CaseType, CaseTypeType, SubmissionFeedback,
} from '../types'
import { useAxiosInstance } from '../api/AxiosInstanceContext'
import { AuthContext } from './authContext'
import { CurrentCohortSubscriptionContext } from './currentCohortSubscriptionContext'

export type SavedCase = CaseType & { notification_status?: 'sent' | 'failed' | 'pending' | 'not_required' };

type SaveCaseProps = Partial<CaseType> & { accepted_waiver_id: number | undefined };

type updateCaseType = { caseId: number, caseName: string, caseType: string, caseCategories: CaseCategoryType[] };

interface CasesContext {
  cases: CaseType[];
  casesError: string | null;
  saveCase: (caseData: SaveCaseProps) => Promise<SavedCase>;
  savedCase: CaseType | null;
  savedCaseError: string | null;
  setSavedCaseError: Dispatch<SetStateAction<string | null>>;
  saveCasePurpose: (caseId: number, purpose: string) => void;
  savedCasePurpose: string | null;
  savedCasePurposeError: string | null;
  setSavedCasePurposeError: Dispatch<SetStateAction<string | null>>;
  refreshCases: () => void;
  getCase: (caseId: number) => CaseType | undefined;
  toggleCaseStatus: (caseId: number, submit: boolean) => Promise<SubmissionFeedback | null>;
  updateCase: (updatePayload: updateCaseType) => Promise<void>;
  deleteCase: ({ caseId }: { caseId: number }) => Promise<void>;
  caseCategories: CaseCategoryType[] | null;
  displayCaseStatuses: CaseStatusType[] | undefined;
  setDisplayCaseStatuses: Dispatch<SetStateAction<CaseStatusType[] | undefined>>;
}

export const CasesContext = createContext<CasesContext>(undefined as any)

interface Props {
  children: React.ReactNode
}

export const CasesProvider: React.FC<Props> = ({ children }) => {
  const axiosInstance = useAxiosInstance();
  const { currentUser } = useContext(AuthContext);
  const { currentCohortSubscription } = useContext(CurrentCohortSubscriptionContext);
  const cohortId = currentCohortSubscription?.cohort.id;
  const scopeKey = `${currentUser?.uid || ''}:${cohortId || ''}`;
  const scopeRef = useRef({ key: scopeKey });
  if (scopeRef.current.key !== scopeKey) scopeRef.current = { key: scopeKey };
  const scope = scopeRef.current;
  const mounted = useRef(true);
  const sequence = useRef(0);
  const [caseState, setCaseState] = useState({ scope, cases: [] as CaseType[] });
  const cases = caseState.scope === scope ? caseState.cases : [];
  const isCurrent = () => mounted.current && scopeRef.current === scope;
  const setCases = (next: SetStateAction<CaseType[]>) => {
    if (isCurrent()) setCaseState(previous => ({ scope, cases: typeof next === 'function' ? next(previous.scope === scope ? previous.cases : []) : next }));
  };
  const [error, setError] = useState<string | null>(null);
  const [savedCase, setSavedCase] = useState<CaseType | null>(null);
  const [savedCaseError, setSavedCaseError] = useState<string | null>(null);
  const [savedCasePurpose, setSavedCasePurpose] = useState<string | null>(null);
  const [savedCasePurposeError, setSavedCasePurposeError] = useState<string | null>(null);
  const [caseCategories, setCaseCategories] = useState<CaseCategoryType[] | null>(null);
  const [displayCaseStatuses, setDisplayCaseStatuses] = useState<CaseStatusType[] | undefined>();

  const getCasesData = () => {
    if (!isCurrent() || !currentUser || !cohortId) return;
    const requestId = ++sequence.current;
    const config = { headers: { 'X-Cohort-Id': String(cohortId) } };
    const valid = () => isCurrent() && sequence.current === requestId;
    setError(null);
    axiosInstance.get('/cases', config)
      .then((response) => { if (valid()) setCases(response.data); })
      .catch(() => {
        if (valid()) {
          setError('Unable to load cases. Please retry.');
          setCases([]);
        }
      });

    axiosInstance.get('/case_categories', config)
      .then((response) => { if (valid()) setCaseCategories(response.data); })
      .catch(() => { if (valid()) setCaseCategories([]); })

    axiosInstance.get('/case_display_statuses', config)
      .then((response) => { if (valid()) setDisplayCaseStatuses(response.data); })
      .catch(() => { if (valid()) setDisplayCaseStatuses([]); });
  }

  useEffect(() => {
    mounted.current = true;
    setSavedCase(null);
    setCaseCategories(null);
    setDisplayCaseStatuses(undefined);
    getCasesData();
    return () => { mounted.current = false; sequence.current++; };
  }, [scopeKey, axiosInstance])

  const refreshCases = () => {
    getCasesData();
  };
  const reloadRef = useRef(getCasesData);
  reloadRef.current = getCasesData;

  const saveCase = async (caseData: Partial<SaveCaseProps>) => {
    if (!isCurrent() || !cohortId || !currentUser) throw new Error('Case selection is no longer current');
    const { data } = await axiosInstance.post<SavedCase>('/case/', JSON.stringify({ ...caseData }), { headers: { 'X-Cohort-Id': String(cohortId) } });
    if (!data?.id) throw new Error('Unable to confirm case save');
    if (isCurrent()) {
      setCases(previous => previous.some(item => item.id === data.id) ? previous.map(item => item.id === data.id ? data : item) : [...previous, data]);
      setSavedCase(data);
    }
    if (mounted.current && scopeRef.current.key === scopeKey) reloadRef.current();
    return data;
  }

  // Errors propagate: the dialog has to know the save failed, or it goes on to
  // submit the case with the values the server just rejected.
  const updateCase = async ({ caseId, caseName, caseType, caseCategories }: updateCaseType) => {
    const { data } = await axiosInstance.post(
      '/update_case/',
      JSON.stringify({
        case_id: caseId,
        case_name: caseName,
        case_type: caseType,
        case_categories: caseCategories,
      }));
    setCases(cases.map((c) => (c.id === caseId ? data : c)));
  }

  const deleteCase = async ({ caseId }: { caseId: number }) => {
    if (!isCurrent() || !cohortId || !currentUser) throw new Error('Case selection is no longer current');
    await axiosInstance.delete(
      '/case/',
      {
        headers: { 'X-Cohort-Id': String(cohortId) },
        data: JSON.stringify({
          case_id: caseId,
        })
      });
    setCases(previous => previous.filter(item => item.id !== caseId));
    if (mounted.current && scopeRef.current.key === scopeKey) reloadRef.current();
  }

  const saveCasePurpose = (caseId: number, casePurpose: string) => {
    axiosInstance.post('/case_purpose/', JSON.stringify({ id: caseId, purpose: casePurpose }))
      .then(({ data }) => {
        if (data) {
          const casesIdsArray = cases.map((obj: CaseType) => obj.id);
          if (casesIdsArray.includes(data.id)) {
            const newCasesArray = cases.map((item: CaseType) =>
              item.id === data.id ? data : item,
            )
            setCases(newCasesArray);
          } else {
            setCases([...cases, ...[data]])
          }
          setSavedCase(data);
          return data;
        }
        return null;
      })
      .catch((e) => {
        console.log('save case purpose error: ' + e);
      })

  }


  const toggleCaseStatus = async (
    caseId: number,
    submit: boolean,
  ): Promise<SubmissionFeedback | null> => {
    try {
      const { data } = await axiosInstance.post(
        '/add_case_status/',
        JSON.stringify({
          case_id: caseId,
          submit: submit,
        }),
      );
      // `advisory_reasons` rides alongside the status fields; strip it so it
      // never lands in the statuses array.
      const { advisory_reasons: advisories, ...status } = data ?? {};
      const updatedCases = cases.map((c) => {
        if (c.id === caseId) {
          c.statuses.push(status as CaseStatusType);
        }
        return c;
      });
      setCases(updatedCases);
      return Array.isArray(advisories) && advisories.length > 0
        ? {
          blocked: false,
          case_id: caseId,
          blocking_reasons: [],
          advisory_reasons: advisories,
        }
        : null;
    } catch (e) {
      // 400 with { blocked: true, blocking_reasons, advisory_reasons } is
      // the quota-gate response from HealProfileAddCaseStatusView. Surface
      // the payload so the dialog can render an inline explanation instead
      // of a generic error toast.
      if (axios.isAxiosError(e) && e.response?.status === 400) {
        const body = e.response.data as Partial<SubmissionFeedback> | undefined;
        if (body && body.blocked && Array.isArray(body.blocking_reasons)) {
          return {
            blocked: true,
            case_id: body.case_id ?? caseId,
            blocking_reasons: body.blocking_reasons,
            advisory_reasons: Array.isArray(body.advisory_reasons) ? body.advisory_reasons : [],
          };
        }
      }
      // Returning null here would be read as a clean success and close the
      // dialog. The request never got a verdict, so say that instead.
      return {
        blocked: true,
        failed: true,
        case_id: caseId,
        blocking_reasons: [],
        advisory_reasons: [],
      };
    }
  }

  const getCase = (caseId: number) => {
    if (caseId && cases && cases !== undefined) {
      return cases.find((c: CaseType) => c.id === caseId);
    }
  };


  const value = {
    cases: cases ? (cases as CaseType[]) : ([] as CaseType[]),
    casesError: error,
    saveCase,
    savedCase,
    savedCaseError: savedCaseError,
    setSavedCaseError,
    saveCasePurpose,
    savedCasePurpose,
    setSavedCasePurpose,
    savedCasePurposeError,
    setSavedCasePurposeError,
    refreshCases,
    getCase,
    toggleCaseStatus,
    updateCase,
    deleteCase,
    caseCategories,
    displayCaseStatuses,
    setDisplayCaseStatuses
  }

  return <CasesContext.Provider value={value}>{children}</CasesContext.Provider>
}